import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import {
  DRAFT_EVENT,
  EMPTY_STRING,
  ONE_HOUR_IN_MS,
  eventBookingStatus,
  TIME_MARGIN_IN_EVENT,
  eventBookingConstants,
  defaultEventSetupObject,
} from "../../../constants";
import {
  deepClone,
  formatCustomDate,
  isLocationsEqual,
  removeShutterBFromEvent,
  updateShutterBConflicts,
  markShutterBUnselectedInDraftEvent,
} from "../../../shared";
import {
  eventBookingActions,
  shutterbProfileActions,
} from "../../../redux/actions";
import DraftEvent from "../EventBooking/DraftEvent/DraftEvent";
import ShutterBProfileView from "../ShutterBProfile/ShutterBProfileView";
import NotifyAdminEditEvent from "../EventBooking/DraftEvent/NotifyAdminEditEvent";
import ShutterBSelectionCard from "../../../shared/components/Cards/ShutterBSelectionCard";

const { TABS_ID } = eventBookingConstants;

const ShutterBSelectionPage = (props) => {
  const spinner = useMemo(
    () =>
      props.spinnerActions.findIndex(
        (f) => f === eventBookingConstants.GET_AVAILABLE_SHUTTER_BS
      ),
    [props.spinnerActions]
  );

  const [isProfileViewShutterBSelected, setIsProfileViewShutterBSelected] =
    useState(false);

  useEffect(() => {
    if (props.activeTab === TABS_ID.SELECT_SHUTTERB) {
      if (!props.formState?.length || !props.availableShutterBs?.length) return;
      const { updatedFormState, updatedAvailableShutterBs } =
        updateShutterBConflicts({
          formState: props.formState,
          originalFormState: props.originalFormState,
          availableShutterBs: props.availableShutterBs,
          timeMargin: TIME_MARGIN_IN_EVENT,
          oneHourMs: ONE_HOUR_IN_MS,
        });

      // Reset isSaveAsDraft if shutterBs are now available
      const formStateWithDraftReset = updatedFormState.map((event) => {
        const matchedAvailable = updatedAvailableShutterBs.find(
          (shutterEvent) => shutterEvent.eventId === event.id
        );

        // Check if there's any shutterB that's available for selection
        const hasSelectableShutterB =
          matchedAvailable &&
          matchedAvailable.items.some((item) => !item.removeShutterB);
        if (hasSelectableShutterB) {
          return {
            ...event,
            isSaveAsDraft: false,
            status:
              event.status === eventBookingStatus.NOTIFY_ADMIN
                ? eventBookingStatus.EDITED
                : event.status,
          };
        }

        return event;
      });

      // Prevent unnecessary setState to avoid infinite loop
      const formStateChanged =
        JSON.stringify(formStateWithDraftReset) !==
        JSON.stringify(props.originalFormState);

      const availableChanged =
        JSON.stringify(updatedAvailableShutterBs) !==
        JSON.stringify(props.availableShutterBs);

      if (formStateChanged) {
        props.setFormState(formStateWithDraftReset);
      }

      if (availableChanged) {
        props.setAvaliableShutterB(updatedAvailableShutterBs);
      }
    }

    // eslint-disable-next-line
  }, [props.formState, props.availableShutterBs, props.activeTab]);

  // Toggle selection of a shutterB
  const toggleSelectShutterB = (eventId, shutterB) => {
    const event = props.formState.find((event) => event.id === eventId);
    const isAlreadySelected = props.isDraftEvent
      ? shutterB?.isSelected
      : event?.orderItems?.some(
          (item) =>
            (item.sbUserId === shutterB.sbUserId &&
              item?.status !== eventBookingStatus.CANCEL) ||
            item.isSelected
        );

    if (isAlreadySelected) {
      deselectShutterB(shutterB, eventId, props.isDraftEvent);
    } else {
      selectShutterB(eventId, shutterB, props.isDraftEvent);
    }
  };

  const selectShutterB = (selectedEventId, shutterB, isDraftEvent) => {
    const selectedEvent = props.formState.find((e) => e.id === selectedEventId);
    if (!selectedEvent) return;
    const shutterBId = shutterB.sbUserId;

    // If it is a draft event skip overlap logic (just set the isSelected to false)
    if (isDraftEvent) {
      const draftFormState = props.formState.map((event) => {
        if (event.id === selectedEventId) {
          return {
            ...event,
            orderItems: (event.orderItems || []).map((item) =>
              item.sbUserId === shutterBId
                ? { ...item, isSelected: true }
                : item
            ),
          };
        }
        return event;
      });
      props.setFormState(draftFormState);
      return;
    }

    const { sbStartTime, duration, eventLocation } = selectedEvent;
    const baseLocation = eventLocation || EMPTY_STRING;
    const selectedStart = new Date(sbStartTime).getTime();
    const durationMs = duration * ONE_HOUR_IN_MS;
    const timeMarginMs = TIME_MARGIN_IN_EVENT * ONE_HOUR_IN_MS;

    const updatedFormState = props.originalFormState.map((event) => {
      if (
        event.id === selectedEventId ||
        event.status === eventBookingStatus.CANCEL
      )
        return event;
      const eventStart = new Date(event.sbStartTime).getTime();
      const eventEnd = eventStart + event.duration * ONE_HOUR_IN_MS;

      let timeStart, timeEnd;
      if (isLocationsEqual(event.eventLocation, baseLocation)) {
        timeStart = selectedStart;
        timeEnd = selectedStart + durationMs;
      } else {
        timeStart = selectedStart - timeMarginMs;
        timeEnd = selectedStart + durationMs + timeMarginMs;
      }

      const hasConflict =
        (eventStart >= timeStart && eventStart <= timeEnd) ||
        (eventEnd >= timeStart && eventEnd <= timeEnd);

      const availableSB =
        props.availableShutterBs.find((e) => e.eventId === selectedEventId)
          ?.items || [];
      const hasShutterB = availableSB?.some(
        (item) =>
          item.sbUserId === shutterBId &&
          item.status !== eventBookingStatus.CANCEL
      );

      if (hasConflict && hasShutterB) {
        return {
          ...event,
          orderItems: (event.orderItems || []).filter(
            (item) => item.sbUserId !== shutterBId
          ),
        };
      }

      return event;
    });

    const finalFormState = updatedFormState.map((event) => {
      if (event.id === selectedEventId) {
        const existingShutterB = (event.orderItems || []).map((item) => {
          if (item.sbUserId === shutterB.sbUserId) {
            // If again selecting same item previously cancelled - restore same item
            if (item.status === eventBookingStatus.CANCEL) {
              return {
                ...item,
                status: eventBookingStatus.EDITED,
                isActive: true,
              };
            }

            // Already exists and active - keep it as is
            return item;
          }

          return item;
        });

        const alreadyExists = existingShutterB.some(
          (item) => item.sbUserId === shutterBId
        );

        return {
          ...event,
          orderItems: alreadyExists
            ? existingShutterB
            : [...existingShutterB, shutterB],
        };
      }
      return event;
    });
    if (
      JSON.stringify(finalFormState) !== JSON.stringify(props.originalFormState)
    ) {
      props.setFormState(finalFormState);
    }
  };

  // Restore shutterB to previously removed from events
  const deselectShutterB = (shutterB, eventId, isDraftEvent) => {
    const shutterBId = shutterB.sbUserId;
    // For draft events, do not remove the shutterB from orderItems
    // Instead, just mark it as not selected (isSelected: false)
    if (isDraftEvent) {
      const updatedFormState = markShutterBUnselectedInDraftEvent(
        props.formState,
        eventId,
        shutterBId
      );
      props.setFormState(updatedFormState);
      return;
    }

    const updatedFormState = removeShutterBFromEvent(
      props.originalFormState,
      eventId,
      shutterBId
    );

    if (
      JSON.stringify(updatedFormState) !==
      JSON.stringify(props.originalFormState)
    ) {
      props.setFormState(updatedFormState);
    }
  };

  const handleViewProfileClick = (shutterbId, isSelected) => {
    setIsProfileViewShutterBSelected(isSelected);
    props.setIsProfileViewShow(true);
    props.getShutterBProfile(shutterbId);
  };

  const handleDraft = (value) => {
    let updatedFormState = [...props.originalFormState];
    if (value === DRAFT_EVENT.DRAFT) {
      updatedFormState = props.originalFormState.map((event) =>
        event.id === props.selectedEventId
          ? { ...event, isSaveAsDraft: true, eventType: {}, orderItems: [] }
          : event
      );
    } else if (value === DRAFT_EVENT.DISCARD) {
      // Remove the selected event from formState
      updatedFormState = props.originalFormState.filter(
        (event) => event.id !== props.selectedEventId
      );
    }
    props.setFormState(updatedFormState);

    // Find the current index in the old formState
    const currentIndex = props.formState.findIndex(
      (event) => event.id === props.selectedEventId
    );

    // Find the next event in the updatedFormState
    let nextEvent = null;
    if (value === DRAFT_EVENT.DISCARD) {
      // After discard, next event is at the same index (since current is removed)
      if (updatedFormState.length > currentIndex) {
        nextEvent = updatedFormState[currentIndex];
      }
    } else {
      // After draft, next event is at the next index
      if (updatedFormState.length > currentIndex + 1) {
        nextEvent = updatedFormState[currentIndex + 1];
      }
    }

    if (nextEvent) {
      props.setSelectedEventId(nextEvent.id);
      props.setEventName(nextEvent.eventName);
    } else {
      if (value === DRAFT_EVENT.DISCARD) {
        // If only one event existed and it was discarded
        // Move to event setup tab and reset formState
        if (props.formState.length === 1) {
          props.resetAvFormKey();
          props.setFormState([deepClone(defaultEventSetupObject)]);
          props.handlePrevious();
          return;
        }
      }

      props.handleNextEvent(updatedFormState);
    }
  };

  const handleDraftEditEvent = () => {
    let updatedFormState = props.originalFormState.map((event) =>
      event.id === props.selectedEventId
        ? {
            ...event,
            status: eventBookingStatus.NOTIFY_ADMIN,
            orderItems: event.orderItems?.map((item) => ({
              ...item,
              status: eventBookingStatus.NOTIFY_ADMIN,
            })),
          }
        : event
    );
    props.setFormState(updatedFormState);
  };

  const selectedFormEvent = Array.isArray(props.formState)
    ? props.formState.find((e) => e.id === props.selectedEventId)
    : null;

  const shutterBItems = useMemo(() => {
    if (props.activeTab !== TABS_ID.SELECT_SHUTTERB) return [];

    const getFilteredItemsForEvent = (eventId, isDraftEvent) => {
      if (isDraftEvent) {
        const matchedForm = props.formState?.find(
          (event) => event.id === eventId
        );
        return matchedForm?.orderItems || [];
      }

      const matchedAvailable = props.availableShutterBs.find(
        (shutterEvent) => shutterEvent.eventId === eventId
      );

      if (!matchedAvailable) return [];

      return matchedAvailable.items.filter(
        (shutterB) => !shutterB.removeShutterB
      );
    };

    return getFilteredItemsForEvent(props.selectedEventId, props.isDraftEvent);
  }, [
    props.activeTab,
    props.selectedEventId,
    props.isDraftEvent,
    props.formState,
    props.availableShutterBs,
  ]);

  // Check if there are no shutterB items and set continue button visibility
  const hasNoShutterBItems = () => {
    const noItems = shutterBItems.length === 0;
    // if (props.setIsHideContinueButton) {
    //   props.setIsHideContinueButton(noItems);
    // }
    return noItems;
  };

  const orderItems = selectedFormEvent?.orderItems ?? [];

  // Build a map: sbUserId -> active items
  const activeOrderItemsMap = useMemo(() => {
    const map = new Map();

    orderItems.forEach((item) => {
      if (item?.status !== eventBookingStatus.CANCEL) {
        if (!map.has(item.sbUserId)) {
          map.set(item.sbUserId, true);
        }
      }
    });

    return map;
  }, [orderItems]);

  return (
    <div>
      {props.isProfileViewShow ? (
        <ShutterBProfileView
          formState={props.formState}
          handleShutterBClick={toggleSelectShutterB}
          isSelected={isProfileViewShutterBSelected}
          setIsProfileViewShutterBSelected={setIsProfileViewShutterBSelected}
          handlePrevious={props.handlePrevious}
        />
      ) : (
        <>
          <div>
            <div className="shutterb-address text-center mt-3">
              {shutterBItems.length > 0 && (
                <p className="font-weight-bold mb-1">
                  select one or more shutterbz to capture this event
                </p>
              )}
              <p className="address-text">
                {selectedFormEvent?.eventLocation?.currentLocation ||
                  EMPTY_STRING}
              </p>
              <p className="address-text">
                {selectedFormEvent?.duration || 0} Hour Reservation
              </p>
              <p className="address-text">
                {selectedFormEvent?.sbStartTime
                  ? formatCustomDate(selectedFormEvent.sbStartTime)
                  : EMPTY_STRING}
              </p>
            </div>

            {spinner === -1 && (
              <div className="row">
                {hasNoShutterBItems() ? (
                  selectedFormEvent?.status === eventBookingStatus.EDITED ||
                  selectedFormEvent?.status ===
                    eventBookingStatus.NOTIFY_ADMIN ? (
                    <NotifyAdminEditEvent
                      handleDraftEditEvent={handleDraftEditEvent}
                      selectedFormEvent={selectedFormEvent}
                    />
                  ) : (
                    <DraftEvent
                      handleDraft={handleDraft}
                      selectedFormEvent={selectedFormEvent}
                    />
                  )
                ) : (
                  shutterBItems.map((shutterB) => {
                    const isSelected = props.isDraftEvent
                      ? shutterB.isSelected
                      : activeOrderItemsMap.has(shutterB.sbUserId);

                    return (
                      <div
                        key={shutterB.sbUserId}
                        className="col-12 col-md-6 col-custom-6 mt-2 mb-3"
                      >
                        <ShutterBSelectionCard
                          shutterB={shutterB}
                          isSelected={isSelected}
                          onSelect={() =>
                            toggleSelectShutterB(
                              props.selectedEventId,
                              shutterB
                            )
                          }
                          onViewProfileClick={handleViewProfileClick}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  spinnerActions: state.system.spinnerActions,
  isEditEvent: state.eventBooking.isEditEvent,
  isDraftEvent: state.eventBooking.isDraftEvent,
  originalFormState: state.eventBooking.formState,
  selectedEventId: state.eventBooking.selectedEventId,
  isProfileViewShow: state.eventBooking.isProfileViewShow,
  availableShutterBs: state.eventBooking.availableShutterBs,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setIsProfileViewShow: (data) =>
    dispatch(eventBookingActions.setIsProfileViewShow(data)),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  getShutterBProfile: (id) =>
    dispatch(shutterbProfileActions.getShutterBProfile(id)),
  setAvaliableShutterB: (data) =>
    dispatch(eventBookingActions.setAvaliableShutterB(data)),
  setEventName: (data) => dispatch(eventBookingActions.setEventName(data)),
  setSelectedEventId: (data) =>
    dispatch(eventBookingActions.setSelectedEventId(data)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ShutterBSelectionPage);
